import React from 'react';
import { connect } from 'react-redux';

import EligibilityOverview from '@components/individualPortal/eligibilityResults/EligibilityOverview';
import Tooltip from '@common-components/tooltip/Tooltip';
import { ALL_ELIGIBILITY_TYPES } from '@constants/individualPortal/dashboard/dashboardModelTypes';
import { renderAmount } from '@utils/dashboard/processCommon';
import { FormattedMessage } from 'react-intl';
import { capitalize, uniq } from '@utils/miscellaneous/objectHelpers';

export function EligibilityOverviewContainerView(props) {
    const {
        eligibilityResults: {
            applicationDate,
            applicationYear,
            caseNumber,
            isFinancial,
            stateSubsidy,
            taxHouseholdDetails
        },
        environment: {
            isState,
            medicaidFullName,
            exchangeName
        },
        processedEligibilityData,
        additionalNote,
        showSummaryHeader,
        isMaintainFATextVisible,
        isBHPEligibleTextVisible,
    } = props;

    /**
     * Helper function, takes aptc, name from taxHouseholdGroup and stateSubsidy from eligibilityResults.
     * @returns {object of replacement parameters for i18n}
     * @param getReplacements
    //  */


    const getReplacements = (aptc, stateSubsidy, name) => ({
        aptcTooltip: <Tooltip
            id='indportal.dashboard.householdSection.eligibility.status.eligibleAptc'
            content='household_section.tooltip_aptc'
            index='eligibility_overview'
            replacement={{
                strong: chunks => <strong>{chunks}</strong>
            }}
        />,
        stateSubsidyTooltip: <Tooltip
            id='household_section.eligibility.status.eligible_state_subsidy'
            content='household_section.tooltip_state_subsidy'
            index='eligibility_overview'
            dataAutomationId='state_subsidy_eligibility_overview'
        />,
        csrTooltip: <Tooltip
            id='household_section.eligibility.status.csr'
            content='household_section.tooltip_csr'
            index='eligibility_overview'
            replacement={{
                strong: chunks => <strong>{chunks}</strong>
            }}
        />,
        coinsuranceTooltip: <Tooltip
            id='common.coinsurance'
            content='household_section.tooltip_coinsurance'
            index='eligibility_overview'
        />,
        copaymentTooltip: <Tooltip
            id='common.copayment'
            content='household_section.tooltip_copayment'
            index='eligibility_overview'
            replacement={{
                strong: chunks => <strong>{chunks}</strong>
            }}
        />,
        deductibleTooltip: <Tooltip
            id='common.deductible'
            content='household_section.tooltip_deductible'
            index='eligibility_overview'
            replacement={{
                strong: chunks => <strong>{chunks}</strong>
            }}
        />,
        stateSubsidy2Tooltip: <Tooltip
            id='householdSection.eligibility.status.stateSubsidy2'
            content='householdSection.tooltipStateSubsidy2'
            index='state_subsidy_2_eligibility_overview'
        />,
        stateSubsidy3Tooltip: <Tooltip
            id='householdSection.eligibility.status.stateSubsidy3'
            content='householdSection.tooltipStateSubsidy3'
            index='state_subsidy_3_eligibility_overview'
        />,
        aptcAmount: renderAmount(aptc),
        turquoisePlansTooltip: <Tooltip
            id='householdSection.eligibility.status.turquoisePlans'
            content='householdSection.tooltipTurquoisePlans'
            index='eligibility_overview_turquoise'
            dataAutomationId='turquoise_plans_eligibility_overview'
        />,
        stateSubsidyAmount: renderAmount(stateSubsidy),
        members: name
    });

    /**
     * Main logic for EligibilityOverview component.
     * @param data
     * @returns {*}
     */
    const getEligibilityOverview = data => {
        if (!data) return false;

        const { QHP, APTC, STATE_SUBSIDY, CSR, CSR_TURQUOISE, ASSESSED_MEDICAID, ASSESSED_CHIP, MEDICAID_OR_CHIP, STATE_SUBSIDY_2, STATE_SUBSIDY_3, BHP, ASSESSED_BHP } = ALL_ELIGIBILITY_TYPES;

        const processedApplicantInfo = data.reduce((acc, item) => {
            if (!acc[item.rawData.taxHouseholdGroup]) {
                acc[item.rawData.taxHouseholdGroup] = { aptc: 0, eligibilities: [], name: '' };
            }
            let memberName = '';
            /*Only if taxHouseholdGroup is not 'NON_ITF' print the applicant who is seeking coverage in the taxHouseholdGroup */
            if (item.rawData.taxHouseholdGroup !== 'NON_ITF' && item.rawData.seekingCoverage && item.rawData.firstName && item.rawData.lastName) {
                memberName = capitalize(item.rawData.firstName) + ' ' + capitalize(item.rawData.lastName);
            } else {
                if (item.rawData.taxHouseholdGroup === 'NON_ITF') {
                    memberName = 'members';
                }

            }
            if (item.rawData.seekingCoverage) {
                acc[item.rawData.taxHouseholdGroup] = {
                    name: memberName,
                    aptc: acc[item.rawData.taxHouseholdGroup].aptc + item.rawData.aptc,
                    eligibilities: [...acc[item.rawData.taxHouseholdGroup].eligibilities, ...item.eligibilities]
                };
            }

            return acc;
        }, {});

        Object.keys(processedApplicantInfo).forEach((key) => {
            let eligibilities = uniq(processedApplicantInfo[key].eligibilities);
            // Per-member processing can add CSR for one applicant and CSR_TURQUOISE for another in the
            // same tax group. The overview must show a single CSR bullet: prefer Turquoise when any member has it.
            if (eligibilities.includes(CSR_TURQUOISE) && eligibilities.includes(CSR)) {
                eligibilities = eligibilities.filter(program => program !== CSR);
            }
            processedApplicantInfo[key].eligibilities = eligibilities;
        });

        // Order of eligibilities which should be displayed for financial and non financial application.
        const ELIGIBILITY_FOR_OVERVIEW_NON_FINANCIAL = [QHP, CSR, CSR_TURQUOISE];
        const ELIGIBILITY_FOR_OVERVIEW_FINANCIAL = [QHP, APTC, STATE_SUBSIDY, STATE_SUBSIDY_2, STATE_SUBSIDY_3, CSR, CSR_TURQUOISE, ASSESSED_MEDICAID, ASSESSED_CHIP, MEDICAID_OR_CHIP, BHP, ASSESSED_BHP];
        const ELIGIBILITY_FOR_OVERVIEW = isFinancial ? ELIGIBILITY_FOR_OVERVIEW_FINANCIAL : ELIGIBILITY_FOR_OVERVIEW_NON_FINANCIAL;

        const ELIGIBILITY_OVERVIEW_KEYS = {
            QHP: 'eligibility_results.overview.qhp',
            APTC: 'eligibility_results.overview.aptc',
            STATE_SUBSIDY: 'eligibility_results.overview.state_subsidy',
            CSR: 'eligibility_results.overview.csr',
            CSR_TURQUOISE: 'eligibilityResults.overview.csrWithTurquoise',
            ASSESSED_MEDICAID: 'eligibility_results.overview.assessed_medicaid',
            ASSESSED_CHIP: 'eligibility_results.overview.assessed_chip',
            MEDICAID_OR_CHIP: 'eligibility_results.overview.assessed_medicaid_or_chip',
            STATE_SUBSIDY_2: 'eligibilityResults.overview.stateSubsidy2',
            STATE_SUBSIDY_3: 'eligibilityResults.overview.stateSubsidy3',
            BHP: 'eligibility_results.overview.bhp',
            ASSESSED_BHP: 'eligibility_results.overview.assessedBhp',
        };

        let eligibilityList = {};
        Object.entries(processedApplicantInfo).forEach(([key, value]) => {

            let programArray = [];
            value.eligibilities.length > 0 && value.eligibilities.forEach(program => {
                if ((program !== 'NOT_QHP' || program !== 'NOT_SEEKING') && ELIGIBILITY_FOR_OVERVIEW.includes(program)) {
                    programArray.push(
                        <FormattedMessage
                            key={program}
                            id={ELIGIBILITY_OVERVIEW_KEYS[program]}
                            values={getReplacements(value.aptc, stateSubsidy, value.name)}
                        />
                    );
                }
            });
            eligibilityList = { ...eligibilityList, [key]: programArray };
        });

        const orderedEligibilityList = Object.entries(eligibilityList).reduce((newObj, [key, value]) => {
            if (value.length) {
                newObj[key] = value;
            }
            return newObj;
        }, {});
        return {
            orderedEligibilityList,
            processedApplicantInfo
        };
    };

    return (
        <EligibilityOverview
            applicationDate={applicationDate}
            caseNumber={caseNumber}
            coverageYear={applicationYear}
            data={getEligibilityOverview(processedEligibilityData)}
            isFinancial={isFinancial}
            isState={isState}
            additionalNote={additionalNote}
            showSummaryHeader={showSummaryHeader}
            taxHouseholdDetails={taxHouseholdDetails}
            isMaintainFATextVisible={isMaintainFATextVisible}
            medicaidFullName={medicaidFullName}
            exchangeName={exchangeName}
            isBHPEligibleTextVisible={isBHPEligibleTextVisible}
        />
    );
}

const mapStateToProps = state => ({
    eligibilityResults: state.individualPortal.eligibilityResults,
    environment: state.common.environment
});

export default connect(mapStateToProps)(EligibilityOverviewContainerView);
